/**
 * HISIP-005: Create Profile Modal Component
 * 
 * Modal for creating new Creative Profiles with futuristic styling
 */

import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';

interface CreateProfileModalProps {
  onSubmit: (data: { name: string; description?: string }) => void;
  onClose: () => void;
}

const CreateProfileModal: React.FC<CreateProfileModalProps> = ({ onSubmit, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) return;

    setIsSubmitting(true);
    
    try {
      await onSubmit({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={handleBackgroundClick}
    >
      <div className="w-full max-w-md bg-black/90 backdrop-blur-xl border border-cyan-500/30 rounded-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-cyan-500/20">
          <div>
            <h2 className="text-lg font-mono text-cyan-400 uppercase tracking-wider">
              Create Profile
            </h2>
            <p className="text-xs text-white/50 font-mono mt-1">
              Configure new creative profile
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/40 hover:text-white/60 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Name Field */}
          <div>
            <label className="block text-xs font-mono text-cyan-400 mb-2 uppercase tracking-wider">
              Profile Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full bg-black/50 border border-cyan-500/30 rounded-lg p-3
                       text-white/90 font-mono text-sm
                       focus:outline-none focus:border-cyan-500/50 focus:bg-black/70
                       transition-all placeholder:text-white/30"
              placeholder="e.g., Cinematic Style"
              maxLength={100}
              required
              autoFocus
            />
            <p className="text-xs text-white/40 mt-1 font-mono">
              Choose a descriptive name for this profile
            </p>
          </div>

          {/* Description Field */}
          <div>
            <label className="block text-xs font-mono text-cyan-400 mb-2 uppercase tracking-wider">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full bg-black/50 border border-cyan-500/30 rounded-lg p-3
                       text-white/90 font-mono text-sm
                       focus:outline-none focus:border-cyan-500/50 focus:bg-black/70
                       transition-all placeholder:text-white/30 resize-none"
              placeholder="Optional description for this profile..."
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-white/40 mt-1 font-mono">
              Description will be injected into persona prompts
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-transparent border border-white/20 rounded-lg
                       text-white/60 font-mono text-sm uppercase tracking-wider
                       hover:bg-white/5 hover:text-white/80 transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!formData.name.trim() || isSubmitting}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 
                       border border-cyan-500/50 rounded-lg
                       text-cyan-400 font-mono text-sm uppercase tracking-wider
                       hover:bg-gradient-to-r hover:from-cyan-500/30 hover:to-blue-500/30
                       hover:border-cyan-500/70 transition-all
                       disabled:opacity-50 disabled:cursor-not-allowed
                       flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border border-cyan-400 border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Create Profile
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateProfileModal;